import { Button } from "@/components/ui/button";

export type PageSize = number | "all";

interface ListPagerProps {
  /** Rows on screen right now. */
  shown: number;
  /** Rows matching the current filters. */
  total: number;
  /** ["buyer", "buyers"] */
  noun: [string, string];
  page: number;
  totalPages: number;
  pageSize: PageSize;
  onPageSizeChange: (size: PageSize) => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  /** A refetch is in flight; the buttons wait for it. */
  busy?: boolean;
  /** "Updating…" shows while a refetch runs on top of a loaded page. */
  updating?: boolean;
  testIdPrefix?: string;
  sizes?: number[];
  density?: "comfortable" | "compact";
}

/**
 * The footer every paged list shares: "Showing 50 of 2,231 contacts",
 * a page-size selector beside it, and Previous / Next when not showing all.
 * Extracted from the Buyer Network page so Contacts and any later list read
 * the same way.
 */
export function ListPager({
  shown, total, noun, page, totalPages, pageSize, onPageSizeChange, onPrev, onNext, hasPrev, hasNext,
  busy = false, updating = false, testIdPrefix = "list", sizes = [50, 100, 200],
  density = "comfortable",
}: ListPagerProps) {
  const btnCls = `${density === "compact" ? "h-7 min-h-0" : "h-8"} px-3 text-xs`;
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-1 text-muted-foreground ${density === "compact" ? "py-1.5 text-xs" : "py-3 text-sm"}`}>
      <div className="flex items-center gap-3">
        <p>
          Showing <span className="font-medium text-foreground">{shown.toLocaleString()}</span> of{" "}
          <span className="font-medium text-foreground">{total.toLocaleString()}</span>{" "}
          {total === 1 ? noun[0] : noun[1]}
          {updating && <span className="ml-2 text-xs opacity-60">Updating…</span>}
        </p>
        <label className="flex items-center gap-1.5 text-xs">
          Show:
          <select
            className={`border rounded ${density === "compact" ? "h-7" : "h-8"} px-2 text-xs bg-background`}
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}
            data-testid={`${testIdPrefix}-page-size`}
          >
            {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="all">All</option>
          </select>
        </label>
      </div>
      {pageSize !== "all" && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!hasPrev || busy} onClick={onPrev} className={btnCls}>Previous</Button>
          <span className="text-xs">Page {page} of {totalPages || 1}</span>
          <Button variant="outline" size="sm" disabled={!hasNext || busy} onClick={onNext} className={btnCls}>Next</Button>
        </div>
      )}
    </div>
  );
}
